// REALTOR.ca DDF® Web API Service
// Comprehensive implementation based on official API documentation
// Uses Firebase Cloud Functions as proxy to avoid CORS issues

import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface AccessTokenResponse {
  access_token: string;
  expires_in: number;
  token_type: string;
  scope: string;
}

export interface ODataResponse<T> {
  '@odata.context': string;
  value: T[];
  '@odata.nextLink'?: string;
}

export interface ODataSingleResponse<T = any> {
  '@odata.context': string;
  [key: string]: T | any;
}

// Member Model
export interface Member {
  MemberKey: string | null;
  MemberMlsId: string | null;
  OfficeKey: string | null;
  OfficeNationalAssociationId: string | null;
  JobTitle: string | null;
  MemberAORKey: string | null;
  MemberAddress1: string | null;
  MemberAddress2: string | null;
  MemberFax: string | null;
  MemberFirstName: string | null;
  MemberLastName: string | null;
  MemberMiddleName: string | null;
  MemberNamePrefix: string | null;
  MemberNameSuffix: string | null;
  MemberNationalAssociationId: string | null;
  MemberNickname: string | null;
  MemberOfficePhone: string | null;
  MemberOfficePhoneExt: string | null;
  MemberPostalCode: string | null;
  MemberPager: string | null;
  MemberTollFreePhone: string | null;
  ModificationTimestamp: string | null;
  OriginalEntryTimestamp: string | null;
  MemberCity: string | null;
  MemberAOR: string | null;
  MemberCountry: string | null;
  MemberDesignation: string[] | null;
  MemberLanguages: string[] | null;
  MemberSocialMedia: SocialMedia[] | null;
  Media: Media[] | null;
  MemberStateOrProvince: string | null;
  MemberStatus: string | null;
  MemberType: string | null;
  MemberEmailYN: boolean | null;
}

// Office Model
export interface Office {
  OfficeKey: string | null;
  OfficeMlsId: string | null;
  OfficeAORKey: string | null;
  OfficeNationalAssociationId: string | null;
  FranchiseNationalAssociationId: string | null;
  OfficeBrokerNationalAssociationId: string | null;
  OfficeAddress1: string | null;
  OfficeAddress2: string | null;
  OfficeCity: string | null;
  OfficeFax: string | null;
  OfficeName: string | null;
  OfficePhone: string | null;
  OfficePhoneExt: string | null;
  OfficePostalCode: string | null;
  Media: Media[] | null;
  OfficeSocialMedia: SocialMedia[] | null;
  ModificationTimestamp: string | null;
  OriginalEntryTimestamp: string | null;
  OfficeType: string | null;
  OfficeStateOrProvince: string | null;
  OfficeAOR: string | null;
  OfficeStatus: string | null;
  OfficeCountry: string | null;
}

// Supporting Models
export interface SocialMedia {
  SocialMediaType: string | null;
  SocialMediaKey: string | null;
  SocialMediaValue: string | null;
}

export interface Media {
  MediaCategory: string | null;
  MediaKey: string | null;
  MediaModificationTimestamp: string | null;
  MediaObject: string | null;
  MediaOrder: number | null;
  MediaType: string | null;
  MediaURL: string | null;
}

// Property Model (simplified - full model has many fields)
export interface Property {
  ListingKey: string | null;
  PropertySubType: string | null;
  DocumentsAvailable: string[] | null;
  LeaseAmount: number | null;
  LeaseAmountFrequency: string | null;
  BusinessType: string[] | null;
  ListPrice: number | null;
  PublicRemarks: string | null;
  ListingId: string | null;
  InternetEntireListingDisplayYN: boolean | null;
  StandardStatus: string | null;
  StatusChangeTimestamp: string | null;
  ListOfficeKey: string | null;
  CoListOfficeKey: string | null;
  CoListAgentKey: string | null;
  ListAgentKey: string | null;
  InternetAddressDisplayYN: boolean | null;
  ListingURL: string | null;
  OriginatingSystemName: string | null;
  PhotosCount: number | null;
  PhotosChangeTimestamp: string | null;
  CommonInterest: string | null;
  ListAOR: string | null;
  ListAORKey: string | null;
  UnparsedAddress: string | null;
  PostalCode: string | null;
  SubdivisionName: string | null;
  StateOrProvince: string | null;
  StreetDirPrefix: string | null;
  StreetDirSuffix: string | null;
  StreetName: string | null;
  StreetNumber: string | null;
  StreetSuffix: string | null;
  UnitNumber: string | null;
  Country: string | null;
  City: string | null;
  Directions: string | null;
  Latitude: number | null;
  Longitude: number | null;
  CityRegion: string | null;
  MapCoordinateVerifiedYN: boolean | null;
  GeocodeManualYN: boolean | null;
  ParkingTotal: number | null;
  YearBuilt: number | null;
  BathroomsPartial: number | null;
  BathroomsTotalInteger: number | null;
  BedroomsTotal: number | null;
  BuildingAreaTotal: number | null;
  BuildingAreaUnits: string | null;
  Rooms: PropertyRoom[] | null;
  Media: Media[] | null;
  ModificationTimestamp: string | null;
  OriginalEntryTimestamp: string | null;
  AvailabilityDate: string | null;
  Inclusions: string | null;
  // ... many more fields from the full Property model
  [key: string]: any;
}

export interface PropertyRoom {
  RoomType: string | null;
  RoomLength: number | null;
  RoomWidth: number | null;
  RoomArea: number | null;
  RoomAreaUnits: string | null;
  RoomDescription: string | null;
}

// OData Query Options
export interface ODataQueryOptions {
  $select?: string[];
  $filter?: string;
  $top?: number; // Max 100, default 20
  $skip?: number;
  $orderby?: string;
  $count?: boolean;
}

// ============================================================================
// REALTOR API SERVICE CLASS
// ============================================================================

class RealtorApiService {
  private functions;
  private getDDFOfficesFn;
  private getDDFMembersFn;
  private getDDFPropertiesFn;

  constructor() {
    // Initialize Firebase Functions
    const app = getApp();
    this.functions = getFunctions(app);
    this.getDDFOfficesFn = httpsCallable(this.functions, 'getDDFOffices');
    this.getDDFMembersFn = httpsCallable(this.functions, 'getDDFMembers');
    this.getDDFPropertiesFn = httpsCallable(this.functions, 'getDDFProperties');
  }

  // ============================================================================
  // AUTHENTICATION & API REQUESTS
  // Note: Authentication and API calls are now handled by Firebase Cloud Functions
  // to avoid CORS issues when calling from the browser
  // ============================================================================

  // ============================================================================
  // OFFICE ENDPOINTS
  // ============================================================================

  /**
   * Get all offices with pagination handling
   * Uses Firebase Cloud Function to avoid CORS issues
   */
  async getAllOffices(options?: ODataQueryOptions): Promise<Office[]> {
    try {
      const result = await this.getDDFOfficesFn({
        filter: options?.$filter,
        orderby: options?.$orderby,
        top: options?.$top,
        skip: options?.$skip
      });

      const response = result.data as { success: boolean; data: Office[] };
      if (response.success) {
        return response.data;
      } else {
        throw new Error('Failed to get offices from DDF API');
      }
    } catch (error: any) {
      console.error('Error getting offices:', error);
      throw new Error(`Failed to get offices: ${error.message || 'Unknown error'}`);
    }
  }

  /**
   * Get a single office by OfficeKey
   */
  async getOffice(officeKey: string, options?: Pick<ODataQueryOptions, '$select'>): Promise<Office> {
    const offices = await this.getAllOffices({
      $filter: `OfficeKey eq '${officeKey}'`,
      ...options
    });
    if (offices.length === 0) {
      throw new Error(`Office with key ${officeKey} not found`);
    }
    return offices[0];
  }

  // ============================================================================
  // MEMBER ENDPOINTS
  // ============================================================================

  /**
   * Get all members with pagination handling
   * Uses Firebase Cloud Function to avoid CORS issues
   */
  async getAllMembers(options?: ODataQueryOptions): Promise<Member[]> {
    try {
      const result = await this.getDDFMembersFn({
        filter: options?.$filter,
        orderby: options?.$orderby,
        top: options?.$top,
        skip: options?.$skip
      });

      const response = result.data as { success: boolean; data: Member[] };
      if (response.success) {
        return response.data;
      } else {
        throw new Error('Failed to get members from DDF API');
      }
    } catch (error: any) {
      console.error('Error getting members:', error);
      throw new Error(`Failed to get members: ${error.message || 'Unknown error'}`);
    }
  }

  /**
   * Get a single member by MemberKey
   */
  async getMember(memberKey: string, options?: Pick<ODataQueryOptions, '$select'>): Promise<Member> {
    const members = await this.getAllMembers({
      $filter: `MemberKey eq '${memberKey}'`,
      ...options
    });
    if (members.length === 0) {
      throw new Error(`Member with key ${memberKey} not found`);
    }
    return members[0];
  }

  // ============================================================================
  // PROPERTY ENDPOINTS
  // ============================================================================

  /**
   * Get all properties with pagination handling
   * Uses Firebase Cloud Function to avoid CORS issues
   */
  async getAllProperties(options?: ODataQueryOptions): Promise<Property[]> {
    try {
      const result = await this.getDDFPropertiesFn({
        filter: options?.$filter,
        orderby: options?.$orderby,
        top: options?.$top,
        skip: options?.$skip
      });

      const response = result.data as { success: boolean; data: Property[] };
      if (response.success) {
        return response.data;
      } else {
        throw new Error('Failed to get properties from DDF API');
      }
    } catch (error: any) {
      console.error('Error getting properties:', error);
      // Log more details about the error
      if (error.code) {
        console.error('Firebase error code:', error.code);
      }
      if (error.details) {
        console.error('Firebase error details:', error.details);
      }
      throw new Error(`Failed to get properties: ${error.message || error.code || 'Unknown error'}`);
    }
  }

  /**
   * Get a single property by ListingKey
   */
  async getProperty(listingKey: string, options?: Pick<ODataQueryOptions, '$select'>): Promise<Property> {
    const properties = await this.getAllProperties({
      $filter: `ListingKey eq '${listingKey}'`,
      ...options
    });
    if (properties.length === 0) {
      throw new Error(`Property with key ${listingKey} not found`);
    }
    return properties[0];
  }
}

// ============================================================================
// EXPORT SINGLETON INSTANCE
// ============================================================================

export const realtorApiService = new RealtorApiService();
export default realtorApiService;
